import { Box, Stack } from "@repo/ui/core";
import { motion } from "framer-motion";
import { SlideTitle } from "@components/SlideTitle";

const LAYERS = [
  { id: "base", label: "Base AOSP Image", color: "#60a5fa", desc: "Android 14 + AAOS framework", w: 600 },
  { id: "vendor", label: "Vendor Overlay", color: "#818cf8", desc: "JLR-specific HAL + configs", w: 520 },
  { id: "apps", label: "Application Layer", color: "#a78bfa", desc: "Pre-installed apps + services", w: 440 },
  { id: "config", label: "Configuration Snapshot", color: "#c084fc", desc: "Build targets + runtime state", w: 360 },
];

const TEMPLATES = [
  { label: "Platform Dev", color: "#4ade80", icon: "P" },
  { label: "App Dev", color: "#22d3ee", icon: "A" },
  { label: "QA / Test", color: "#f59e0b", icon: "Q" },
];

export function TemplatesSnapshotsSlide() {
  const layerH = 44;
  const layerGap = 6;
  const stackH = LAYERS.length * (layerH + layerGap);
  const svgW = 1040;
  const stackStartY = 50;
  const stackCenterX = 320;

  const templateStartX = 690;
  const templateY = 50;
  const templateGap = 100;
  const cardH = 70;

  const annotationY = Math.max(
    stackStartY + stackH,
    templateY + (TEMPLATES.length - 1) * templateGap + cardH,
  ) + 28;
  const svgH = annotationY + 70;

  return (
    <Box w="100%" maw={1400} px="xl">
      <Stack gap="md">
        <SlideTitle
          title="Templates & Snapshots"
          subtitle="Clone-on-write image layers — teams self-serve from a template catalog"
        />

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.4 }}
        >
          <svg
            viewBox={`0 0 ${svgW} ${svgH}`}
            width="100%"
            style={{ display: "block", maxHeight: "62vh" }}
            aria-label="Templates and Snapshots architecture showing clone-on-write layers"
          >
            <defs>
              <linearGradient id="ts-arrow-grad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="rgba(255,255,255,0.06)" />
                <stop offset="100%" stopColor="var(--customer-accent)" />
              </linearGradient>
              <marker
                id="ts-arrow"
                viewBox="0 0 10 10"
                refX="9"
                refY="5"
                markerWidth="6"
                markerHeight="6"
                orient="auto"
              >
                <path d="M1 1 L9 5 L1 9" fill="none" stroke="var(--customer-accent)" strokeWidth="2" strokeLinecap="round" />
              </marker>
            </defs>

            {/* Section label: Snapshot Stack */}
            <motion.text
              x={stackCenterX}
              y={stackStartY - 10}
              textAnchor="middle"
              fontSize={11}
              fill="rgba(255,255,255,0.35)"
              fontFamily="inherit"
              fontWeight={600}
              letterSpacing="0.08em"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              SNAPSHOT STACK (CLONE-ON-WRITE)
            </motion.text>

            {/* Layer stack */}
            {LAYERS.map((layer, i) => {
              const y = stackStartY + i * (layerH + layerGap);
              const x = stackCenterX - layer.w / 2;
              return (
                <motion.g
                  key={layer.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 + i * 0.12, duration: 0.4 }}
                >
                  <rect
                    x={x}
                    y={y}
                    width={layer.w}
                    height={layerH}
                    rx={6}
                    fill={layer.color}
                    opacity={0.12}
                    stroke={layer.color}
                    strokeWidth={1.5}
                    strokeOpacity={0.4}
                  />
                  <text
                    x={x + 14}
                    y={y + 18}
                    fontSize={12}
                    fill={layer.color}
                    fontWeight={700}
                    fontFamily="inherit"
                  >
                    {layer.label}
                  </text>
                  <text
                    x={x + 14}
                    y={y + 34}
                    fontSize={10}
                    fill="rgba(255,255,255,0.4)"
                    fontFamily="inherit"
                  >
                    {layer.desc}
                  </text>
                  {/* Layer depth indicator */}
                  <rect
                    x={x}
                    y={y}
                    width={4}
                    height={layerH}
                    rx={2}
                    fill={layer.color}
                    opacity={0.6}
                  />
                </motion.g>
              );
            })}

            {/* Clone arrows from stack to templates */}
            {TEMPLATES.map((tmpl, i) => {
              const fromX = stackCenterX + LAYERS[0]!.w / 2 + 10;
              const fromY = stackStartY + stackH / 2;
              const toX = templateStartX;
              const toY = templateY + i * templateGap + cardH / 2;
              const midX = fromX + (toX - fromX) * 0.5;
              return (
                <motion.path
                  key={`arrow-${tmpl.label}`}
                  d={`M${fromX},${fromY} C${midX},${fromY} ${midX},${toY} ${toX},${toY}`}
                  fill="none"
                  stroke="url(#ts-arrow-grad)"
                  strokeWidth={1.5}
                  strokeDasharray="6 4"
                  markerEnd="url(#ts-arrow)"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  transition={{ delay: 1.1 + i * 0.15, duration: 0.6 }}
                />
              );
            })}

            {/* Section label: Template Catalog */}
            <motion.text
              x={templateStartX + 150}
              y={templateY - 16}
              textAnchor="middle"
              fontSize={11}
              fill="rgba(255,255,255,0.35)"
              fontFamily="inherit"
              fontWeight={600}
              letterSpacing="0.08em"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9 }}
            >
              TEMPLATE CATALOG
            </motion.text>

            {/* Template cards */}
            {TEMPLATES.map((tmpl, i) => {
              const y = templateY + i * templateGap;
              const cardW = 300;
              return (
                <motion.g
                  key={tmpl.label}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.0 + i * 0.12, duration: 0.4 }}
                >
                  <rect
                    x={templateStartX}
                    y={y}
                    width={cardW}
                    height={cardH}
                    rx={8}
                    fill="rgba(255,255,255,0.03)"
                    stroke="rgba(255,255,255,0.08)"
                    strokeWidth={1}
                  />
                  {/* Icon circle */}
                  <circle
                    cx={templateStartX + 30}
                    cy={y + cardH / 2}
                    r={16}
                    fill={tmpl.color}
                    opacity={0.15}
                    stroke={tmpl.color}
                    strokeWidth={1.5}
                    strokeOpacity={0.5}
                  />
                  <text
                    x={templateStartX + 30}
                    y={y + cardH / 2 + 5}
                    textAnchor="middle"
                    fontSize={13}
                    fill={tmpl.color}
                    fontWeight={700}
                    fontFamily="inherit"
                  >
                    {tmpl.icon}
                  </text>
                  {/* Label */}
                  <text
                    x={templateStartX + 58}
                    y={y + 28}
                    fontSize={13}
                    fill={tmpl.color}
                    fontWeight={600}
                    fontFamily="inherit"
                  >
                    {tmpl.label}
                  </text>
                  <text
                    x={templateStartX + 58}
                    y={y + 46}
                    fontSize={10}
                    fill="rgba(255,255,255,0.4)"
                    fontFamily="inherit"
                  >
                    Pre-configured · Ready in minutes
                  </text>
                  {/* Self-serve badge */}
                  <rect
                    x={templateStartX + cardW - 72}
                    y={y + cardH / 2 - 10}
                    width={58}
                    height={20}
                    rx={10}
                    fill={tmpl.color}
                    opacity={0.12}
                  />
                  <text
                    x={templateStartX + cardW - 43}
                    y={y + cardH / 2 + 4}
                    textAnchor="middle"
                    fontSize={9}
                    fill={tmpl.color}
                    fontWeight={600}
                    fontFamily="inherit"
                  >
                    Self-serve
                  </text>
                </motion.g>
              );
            })}

            {/* Bottom annotation */}
            <motion.g
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.8 }}
            >
              <rect
                x={60}
                y={annotationY}
                width={svgW - 120}
                height={50}
                rx={8}
                fill="rgba(74,222,128,0.04)"
                stroke="rgba(74,222,128,0.12)"
                strokeWidth={1}
              />
              <text
                x={svgW / 2}
                y={annotationY + 20}
                textAnchor="middle"
                fontSize={11}
                fill="var(--customer-accent-light)"
                fontWeight={600}
                fontFamily="inherit"
              >
                Clone-on-write: new emulators launch from snapshots in seconds, not hours
              </text>
              <text
                x={svgW / 2}
                y={annotationY + 36}
                textAnchor="middle"
                fontSize={10}
                fill="rgba(255,255,255,0.35)"
                fontFamily="inherit"
              >
                Each team gets isolated copies — changes don&apos;t affect the base image
              </text>
            </motion.g>
          </svg>
        </motion.div>
      </Stack>
    </Box>
  );
}
